import { c as _c } from "react/compiler-runtime";
import type { BigIntish } from '@morpho-org/blue-sdk';
import { type InterfaceToken, toBigInt } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { formatters } from '@morpho-org/ui-v6/utils';
import { memo, useMemo } from 'react';
import type { VaultV2Caps, VaultV2PendingTimelockDecodedData } from '../../types/pendingTimelocks';
import { getTimelockFunctionConfig } from '../utils';
import { StyledVaultPendingTimelock } from '../VaultV2PendingTimelockStyled';
import AdapterItemLink from './AdapterItemLink';
import CollateralTokenLink from './CollateralTokenLink';
import MarketItemLink from './MarketItemLink';
import VaultPendingTimelockHeader from './VaultV2PendingTimelockHeader';
const formatAbsoluteCap = (value: BigIntish, token: InterfaceToken) => formatters.short.default('0').trailingZero(false).digits(2).unit(token.symbol).of(toBigInt(value), token.decimals);
interface VaultV2PendingTimelockChangeAbsoluteCapProps {
  newValue: BigIntish;
  functionName: string;
  capId: string;
  caps?: VaultV2Caps;
  vaultSupplyToken?: InterfaceToken | null;
  data?: Extract<VaultV2PendingTimelockDecodedData, {
    __typename: 'VaultV2IncreaseCapPendingData';
  }>['config']['data'];
}
const VaultV2PendingTimelockChangeAbsoluteCap = t0 => {
  const $ = _c(55);
  const {
    newValue,
    functionName,
    capId,
    caps: t1,
    vaultSupplyToken,
    data
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const caps = t2;
  let t3;
  if ($[2] !== capId || $[3] !== caps) {
    let t4;
    if ($[5] !== capId) {
      t4 = cap => cap.id === capId;
      $[5] = capId;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    t3 = caps.find(t4);
    $[2] = capId;
    $[3] = caps;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const currentCap = t3;
  let t4;
  if ($[7] !== functionName) {
    t4 = getTimelockFunctionConfig({
      functionName
    });
    $[7] = functionName;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const {
    label,
    criticality
  } = t4;
  let t5;
  bb0: {
    if (!currentCap) {
      t5 = null;
      break bb0;
    }
    if (vaultSupplyToken) {
      let t6;
      if ($[9] !== currentCap.absoluteCap || $[10] !== vaultSupplyToken) {
        t6 = formatAbsoluteCap(currentCap.absoluteCap, vaultSupplyToken);
        $[9] = currentCap.absoluteCap;
        $[10] = vaultSupplyToken;
        $[11] = t6;
      } else {
        t6 = $[11];
      }
      t5 = t6;
      break bb0;
    }
    if (currentCap.data?.__typename === "MarketV1CapData") {
      let t6;
      if ($[12] !== currentCap.absoluteCap || $[13] !== currentCap.data.market?.loanAsset.decimals || $[14] !== currentCap.data.market?.loanAsset.symbol) {
        t6 = formatters.short.default("0").trailingZero(false).digits(2).unit(currentCap.data.market?.loanAsset.symbol).of(toBigInt(currentCap.absoluteCap), currentCap.data.market?.loanAsset.decimals);
        $[12] = currentCap.absoluteCap;
        $[13] = currentCap.data.market?.loanAsset.decimals;
        $[14] = currentCap.data.market?.loanAsset.symbol;
        $[15] = t6;
      } else {
        t6 = $[15];
      }
      t5 = t6;
      break bb0;
    } else {
      if (currentCap.data?.__typename === "AdapterCapData") {
        const adapter = currentCap.data.adapter;
        if (adapter?.__typename === "MetaMorphoAdapter") {
          let t6;
          if ($[16] !== adapter.metaMorpho.asset.decimals || $[17] !== adapter.metaMorpho.asset.symbol || $[18] !== currentCap.absoluteCap) {
            t6 = formatters.short.default("0").trailingZero(false).digits(2).unit(adapter.metaMorpho.asset.symbol).of(toBigInt(currentCap.absoluteCap), adapter.metaMorpho.asset.decimals);
            $[16] = adapter.metaMorpho.asset.decimals;
            $[17] = adapter.metaMorpho.asset.symbol;
            $[18] = currentCap.absoluteCap;
            $[19] = t6;
          } else {
            t6 = $[19];
          }
          t5 = t6;
          break bb0;
        }
      }
    }
    t5 = null;
  }
  const formattedCurrentAbsoluteCap = t5;
  let t6;
  bb1: {
    if (vaultSupplyToken) {
      let t7;
      if ($[20] !== newValue || $[21] !== vaultSupplyToken) {
        t7 = formatAbsoluteCap(newValue, vaultSupplyToken);
        $[20] = newValue;
        $[21] = vaultSupplyToken;
        $[22] = t7;
      } else {
        t7 = $[22];
      }
      t6 = t7;
      break bb1;
    }
    if (data?.__typename === "AdapterCapData") {
      if (data.adapter?.__typename === "MetaMorphoAdapter") {
        let t7;
        if ($[23] !== data.adapter.metaMorpho.asset.decimals || $[24] !== data.adapter.metaMorpho.asset.symbol || $[25] !== newValue) {
          t7 = formatters.short.default("0").trailingZero(false).digits(2).unit(data.adapter.metaMorpho.asset.symbol).of(toBigInt(newValue), data.adapter.metaMorpho.asset.decimals);
          $[23] = data.adapter.metaMorpho.asset.decimals;
          $[24] = data.adapter.metaMorpho.asset.symbol;
          $[25] = newValue;
          $[26] = t7;
        } else {
          t7 = $[26];
        }
        t6 = t7;
        break bb1;
      } else {
        if (data.adapter?.__typename === "MorphoMarketV1Adapter") {
          let t7;
          if ($[27] !== data.adapter.vault.asset.decimals || $[28] !== data.adapter.vault.asset.symbol || $[29] !== newValue) {
            t7 = formatters.short.default("0").trailingZero(false).digits(2).unit(data.adapter.vault.asset.symbol).of(toBigInt(newValue), data.adapter.vault.asset.decimals);
            $[27] = data.adapter.vault.asset.decimals;
            $[28] = data.adapter.vault.asset.symbol;
            $[29] = newValue;
            $[30] = t7;
          } else {
            t7 = $[30];
          }
          t6 = t7;
          break bb1;
        }
      }
    } else {
      if (data?.__typename === "MarketV1CapData") {
        let t7;
        if ($[31] !== data.market?.loanAsset.decimals || $[32] !== data.market?.loanAsset.symbol || $[33] !== newValue) {
          t7 = formatters.short.default("0").trailingZero(false).digits(2).unit(data.market?.loanAsset.symbol).of(toBigInt(newValue), data.market?.loanAsset.decimals);
          $[31] = data.market?.loanAsset.decimals;
          $[32] = data.market?.loanAsset.symbol;
          $[33] = newValue;
          $[34] = t7;
        } else {
          t7 = $[34];
        }
        t6 = t7;
        break bb1;
      }
    }
    t6 = null;
  }
  const formattedNewAbsoluteCap = t6;
  let t7;
  if ($[35] !== criticality || $[36] !== label) {
    t7 = <VaultPendingTimelockHeader title={label} criticality={criticality} />;
    $[35] = criticality;
    $[36] = label;
    $[37] = t7;
  } else {
    t7 = $[37];
  }
  let t8;
  if ($[38] !== data) {
    t8 = data?.__typename === "AdapterCapData" && <StyledVaultPendingTimelock.Row><span className="text-2xs text-foreground">Item</span><span className="text-2xs text-foreground min-w-0"><AdapterItemLink adapter={data.adapter} /></span></StyledVaultPendingTimelock.Row>;
    $[38] = data;
    $[39] = t8;
  } else {
    t8 = $[39];
  }
  let t9;
  if ($[40] !== data) {
    t9 = data?.__typename === "MarketV1CapData" && data.adapter && <StyledVaultPendingTimelock.Row><span className="text-2xs text-foreground">Market</span><span className="text-2xs text-foreground"><MarketItemLink market={data.market} /></span></StyledVaultPendingTimelock.Row>;
    $[40] = data;
    $[41] = t9;
  } else {
    t9 = $[41];
  }
  let t10;
  if ($[42] !== data) {
    t10 = data?.__typename === "CollateralCapData" && data.collateralToken && <StyledVaultPendingTimelock.Row><span className="text-2xs text-foreground">Item</span><CollateralTokenLink collateralToken={data.collateralToken} /></StyledVaultPendingTimelock.Row>;
    $[42] = data;
    $[43] = t10;
  } else {
    t10 = $[43];
  }
  let t11;
  if ($[44] !== formattedCurrentAbsoluteCap) {
    t11 = formattedCurrentAbsoluteCap && <StyledVaultPendingTimelock.Row><span className="text-2xs text-foreground">Current cap</span><span className="text-2xs text-foreground">{formattedCurrentAbsoluteCap}</span></StyledVaultPendingTimelock.Row>;
    $[44] = formattedCurrentAbsoluteCap;
    $[45] = t11;
  } else {
    t11 = $[45];
  }
  let t12;
  if ($[46] !== formattedNewAbsoluteCap) {
    t12 = isDefined(formattedNewAbsoluteCap) && <StyledVaultPendingTimelock.Row><span className="text-2xs text-foreground">New cap</span><span className="text-2xs text-foreground">{formattedNewAbsoluteCap}</span></StyledVaultPendingTimelock.Row>;
    $[46] = formattedNewAbsoluteCap;
    $[47] = t12;
  } else {
    t12 = $[47];
  }
  let t13;
  if ($[48] !== t10 || $[49] !== t11 || $[50] !== t12 || $[51] !== t7 || $[52] !== t8 || $[53] !== t9) {
    t13 = <div className="flex flex-col">{t7}{t8}{t9}{t10}{t11}{t12}</div>;
    $[48] = t10;
    $[49] = t11;
    $[50] = t12;
    $[51] = t7;
    $[52] = t8;
    $[53] = t9;
    $[54] = t13;
  } else {
    t13 = $[54];
  }
  return t13;
};
export default memo(VaultV2PendingTimelockChangeAbsoluteCap);